import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { StoragePermissionService } from '../storagePermission/storage-permission.service';
import { Cart, CartLottery, ApplyPromoCode, PriceLottery, FreeProductData, UpdateCart } from '../../models/cart.model';
import { Observable, ReplaySubject } from 'rxjs';
import { GlobalsService } from '../globals/globals.service';
import { LottosService } from '../lottos/lottos.service';
import { TranslateService } from '@ngx-translate/core';
import { mergeMap, tap } from 'rxjs/operators';
import { PickerService } from '../picker/picker.service';
import { Lottery, Offer } from 'src/app/models/offer.model';
import { IFreeProductData } from 'src/app/models/productTempCart';

@Injectable({
  providedIn: 'root'
})
export class CartService {
  public headers: any;
  public apiUrl = environment.rapi;
  public lot_id = environment.lottery.lot_id;
  public pcode: string;
  public lottery: Lottery = environment.lottery;
  public offer: Offer = this.lottery.hasOwnProperty('offer') ? this.lottery.offer : null;
  public ticketsByDraw: number = this.offer && this.offer.hasOwnProperty('multiplier') ? this.offer.multiplier : 1;

  protected cartData: Cart;
  protected cartLotteryData: CartLottery;
  protected applyPcodeData: ApplyPromoCode;

  public lottoPrice: PriceLottery;
  public freeProductData: FreeProductData;
  public productData: IFreeProductData;
  public cartObj: any;

  cartSuccessfully$: Observable<any>;
  protected cartSuccesfullySubject = new ReplaySubject<any>(1);

  constructor(
    private _http: HttpClient,
    private _storageService: StoragePermissionService,
    private _GlobalsService: GlobalsService,
    private _lottoService: LottosService,
    private _translate: TranslateService,
    private _pickerService: PickerService
  ) {
    this.getLottoPrice();
    this.cartSuccessfully$ = this.cartSuccesfullySubject.asObservable();
    this._setPromoCode();
  }

  private _setPromoCode(): void {
    return (this.pcode = environment.pcode[this._GlobalsService.getSystem().site]);
  }

  createCart(): Observable<any> {
    const httpOptions = {
      headers: this.getHeaderSAuth()
    };
    return this._http.post(`${this.apiUrl}/api/carts`, '', httpOptions);
  }

  createCartTemp(): Observable<any> {
    localStorage.setItem('spinnerProgress', String(18));
    this.setCartTemp();
    const httpOptions = {
      headers: this.getHeader()
    };
    const endpointReturn = this._http.post(`${this.apiUrl}/api/carts/free-product/temp`, this.productData, httpOptions);
    localStorage.setItem('spinnerProgress', String(21));
    return endpointReturn;
  }

  createCartTempHalfUser(): Observable<any> {
    this.setCartTemp();
    const profile = this._storageService.getProfile();
    const data ={
      id_half_user: profile.identifier,
      ...this.productData
    }
    return this._http.post(`${this.apiUrl}/api/carts/free-product/temp/half-user`, data);
  }

  assingCartToUser(cart: UpdateCart): Observable<any> {
    const httpOptions = {
      headers: this.getHeader()
    };
    return this._http.put(`${this.apiUrl}/api/carts/${cart.cart}`, cart, httpOptions);
  }

  createCartLottery(data: CartLottery): Observable<any> {
    const httpOptions = {
      headers: this.getHeader()
    };
    return this._http.post(`${this.apiUrl}/api/cart_lotteries`, data, httpOptions);
  }

  applyPromocode(data: ApplyPromoCode): Observable<any> {
    const httpOptions = {
      headers: this.getHeader()
    };
    return this._http.post(`${this.apiUrl}/api/carts/apply_promocode/` + this.cartLotteryData.cart_id, data, httpOptions);
  }

  applyOnlyPromoCode(data: ApplyPromoCode): Observable<any> {
    const httpOptions = {
      headers: this.getHeader()
    };
    return this._http.post(`${this.apiUrl}/api/promocode_info/${data.promo_code}`, data, httpOptions);
  }

  setPromocode(): any {
    if (environment.multisite.view == '.nxnoffer') {
      this._translate.get('promocode').subscribe((value) => {
        let site: string = this._GlobalsService.getSystem().site;
        if (typeof value === 'object') {
          this.pcode = value[`${site}`];
        }
      });
    }
  }

  getToken(): any {
    return this._storageService.getToken();
  }

  getHeader() {
    this.headers = new HttpHeaders({
      'Content-Type': 'application/json; charset=utf-8',
      Accept: 'application/json',
      Authorization: `Bearer ${this.getToken()}`
    });
    return this.headers;
  }

  getHeaderSAuth() {
    this.headers = new HttpHeaders({
      'Content-Type': 'application/json; charset=utf-8',
      Accept: 'application/json'
    });
    return this.headers;
  }

  setCartData(response: any): void {
    this.cartData = {
      ...response.data
    };
  }

  /**
   * Organize the picker object for the lottery cart, if it is for metalate, it receives the modifier parameters for revancha and revanchita.
   * @param picksData
   */
  setCartLotteryData(picks: any[], modifier: any, extraPicks: any[], ticketsByDraw: number): void {
    this.cartLotteryData = {
      cart_id: this.cartData.identifier,
      lottery_id: this.lot_id,
      price_id: this.lottoPrice.identifier,
      pick_type: 3,
      tickets_by_draw: ticketsByDraw,
      draws: 1,
      pick_balls: picks
    };
    if (extraPicks)
      this.cartLotteryData = {
        ...this.cartLotteryData,
        pick_extra_balls: extraPicks
      };
    let env: any = environment;
    if (env.lottery && env.lottery.special_draw)
      this.cartLotteryData = {
        ...this.cartLotteryData,
        special_draw: 1
      };

    if (modifier) {
      this.cartLotteryData = {
        ...this.cartLotteryData,
        price_modifier_1: modifier.price_modifier_1,
        price_modifier_2: modifier.price_modifier_2
      };
    }
  }

  setCartTemp(): void {
    const source = environment.source;
    const product_type = environment.product_type;
    let product_id = environment.product_id;
    const pick_type = environment.pick_type;
    const ticket_by_draw = this.ticketsByDraw;
    const type = environment.type;

    this.productData = {
      source,
    };

    if( type !== 'freeplay' && type !== 'casino-deposit' ){

      this.productData = {
        source,
        products: []
      };

      this.productData.products.push({ product_id, product_type, pick_type, ticket_by_draw });

      if (environment.type === 'nxnoffer' || environment.type === 'discount-product' || environment.type === 'discount-vtemplate') {
        let { base, modifier, extra } = this._pickerService.getUserPicks();
        if (base) {
          this.productData.products[0].pick_balls = base;
        }
        if (modifier) {
          this.productData.products[0].price_modifier_1 = modifier.price_modifier_1;
          this.productData.products[0].price_modifier_2 = modifier.price_modifier_2;
        }
        if (extra) {
          this.productData.products[0].pick_extra_balls = extra;
        }
      }
    }

  }

  protected getLottoPrice(): void {
    this._lottoService.lottoPrice$.subscribe((data) => {
      this.lottoPrice = data;
    });
  }

  protected setCartSuccessSubject(cart): void {
    this.cartSuccesfullySubject.next(cart);
  }

  public createUserCart(basePicks: string[], modifier: any, extraPicks: string[], ticketsByDraw: number = 1): Observable<any> {
    this.setPromocode();

    return this.createCart().pipe(
      tap((cart) => this.setCartData(cart)),
      mergeMap((cart) => this.assingCartToUser({ cart: cart.data.identifier })),
      tap(() => this.setCartLotteryData(basePicks, modifier, extraPicks, ticketsByDraw)),
      mergeMap(() => this.createCartLottery(this.cartLotteryData)),
      mergeMap(() => this.applyPromocode({ promo_code: this.pcode }))
    );
  }
}
